import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import VideoTile from './VideoTile';
import YouTubeTile from './YouTubeTile';
import StreamTile from './StreamTile';
import RemoteTile from './RemoteTile';
import CompactVolumeSlider from './CompactVolumeSlider';
import { subscribeToVolume, setGlobalVolume } from '../utils/globalVolume';
const VideoGrid = ({ localStream, remoteStreams, cameraEnabled, usernames, currentUsername, showUsernames, currentYouTubeVideo, currentMediaVideo, isYouTubeProducer, isHost, socket, onYouTubeControl, onYouTubePlay, onYouTubePause, onYouTubeStop, onYouTubeClick, onYouTubeCommand, featuredMode = false, blurredUsers = new Map(), youtubeStartTime = null, youtubeIsPaused = false, youtubePausedAt = null, isAuthenticated = false, broadcastMode = false, isGloballyMuted = false }) => {
    // Stream state
    const [currentStream, setCurrentStream] = useState(null);
    const [streamProxyUrl, setStreamProxyUrl] = useState('');
    const [streamDirectUrl, setStreamDirectUrl] = useState('');
    const [streamIsHLS, setStreamIsHLS] = useState(false);
    // ADDED: Window width state for responsive layout
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    // ADDED: Broadcast video ready state (like YouTube's playerReady)
    const [broadcastVideoPlaying, setBroadcastVideoPlaying] = useState(false);
    // Volume control for media tiles - synced with global volume
    const [mediaVolume, setMediaVolume] = useState(0);
    const [mediaIsHovered, setMediaIsHovered] = useState(false);
    // Subscribe to global volume changes - always sync
    // Browser will decide if unmuted playback is allowed, not us
    useEffect(() => {
        const unsubscribe = subscribeToVolume((newVolume) => {
            setMediaVolume(newVolume);
        });
        return unsubscribe;
    }, []);
    // Ref to track YouTube auto-play triggers
    const youtubeAutoPlayRef = useRef({
        videoId: null,
        triggered: false
    });
    // ADDED: Window resize listener for responsive layout
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    // REMOVED: User interaction capture is NOT needed for muted videos
    // Muted videos can autoplay without ANY user interaction per browser policy
    // Videos start muted and autoplay immediately - user manually unmutes via slider
    // Handle global mute for NON-broadcast video/audio elements
    // Broadcast videos are controlled entirely by the volume slider (mediaVolume)
    useEffect(() => {
        if (!currentMediaVideo || currentMediaVideo.isBroadcast)
            return;
        const videoElement = document.getElementById(`media-video-${currentMediaVideo.filename}`);
        if (!videoElement)
            return;
        // Non-broadcast videos use global mute state
        videoElement.muted = isGloballyMuted;
        console.log(`ðŸ”‡ Non-broadcast video muted state:`, videoElement.muted);
    }, [isGloballyMuted, currentMediaVideo, broadcastVideoPlaying]);
    // Handle media volume changes and mute state
    useEffect(() => {
        if (!currentMediaVideo)
            return;
        const videoElement = document.getElementById(`media-video-${currentMediaVideo.filename}`);
        if (!videoElement)
            return;
        videoElement.volume = mediaVolume / 100;
        videoElement.muted = mediaVolume === 0;
        console.log(`ðŸ”Š Media volume set to ${mediaVolume}% (${mediaVolume === 0 ? 'muted' : 'unmuted'})`);
    }, [mediaVolume, currentMediaVideo]);
    // Build participants list
    const participants = [];
    // Add local video stream if camera is enabled
    if (cameraEnabled && localStream) {
        participants.push({
            id: 'local',
            type: 'camera',
            stream: localStream,
            username: currentUsername || 'You',
            isLocal: true,
            peerId: socket?.id || 'local',
            streamType: 'camera'
        });
    }
    // Add remote streams
    Object.entries(remoteStreams || {}).forEach(([key, stream]) => {
        const [peerId, streamType] = key.split('-');
        const user = (usernames || {})[peerId];
        const displayName = user?.username || `User ${peerId.slice(0, 4)}`;
        participants.push({
            id: key,
            type: streamType,
            stream,
            username: displayName,
            isLocal: false,
            peerId,
            streamType
        });
    });
    console.log('ðŸŽ¯ VideoGrid rendering with participants:', {
        total: participants.length,
        types: participants.map(p => p.type),
        showUsernames,
        isHost,
        hasSocket: !!socket,
        featuredMode,
        windowWidth,
        blurredUsers: Array.from(blurredUsers),
        participantPeerIds: participants.map(p => ({ peerId: p.peerId, isLocal: p.isLocal, username: p.username }))
    });
    // Listen for stream updates from chat commands
    useEffect(() => {
        if (!socket)
            return;
        const handleStreamUpdate = (data) => {
            console.log('ðŸ“º Stream update received:', data);
            if (data.action === 'stop') {
                setCurrentStream(null);
                setStreamProxyUrl('');
                setStreamDirectUrl('');
                setStreamIsHLS(false);
            }
            else if (data.currentStream) { // âœ… FIXED: Check for currentStream
                setCurrentStream(data.currentStream);
                setStreamProxyUrl('');
                setStreamDirectUrl(data.streamUrl || ''); // âœ… FIXED: Use streamUrl
                setStreamIsHLS(data.isHLS || false);
            }
        };
        socket.on('stream-updated', handleStreamUpdate);
        return () => socket.off('stream-updated', handleStreamUpdate);
    }, [socket]);
    // Auto-play YouTube video for new users
    useEffect(() => {
        if (!currentYouTubeVideo?.isNewUserSync)
            return;
        if (youtubeAutoPlayRef.current.videoId === currentYouTubeVideo.videoId && youtubeAutoPlayRef.current.triggered) {
            return;
        }
        console.log('ðŸŽ¬ New user YouTube auto-play triggered for:', currentYouTubeVideo.videoId);
        youtubeAutoPlayRef.current = {
            videoId: currentYouTubeVideo.videoId,
            triggered: true
        };
        setTimeout(() => {
            const iframe = document.querySelector('iframe[src*="youtube.com"]');
            if (iframe?.contentWindow) {
                console.log('â–¶ï¸ Sending play command to YouTube iframe');
                iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            }
        }, 2000);
    }, [currentYouTubeVideo]);
    // Restart broadcast video when it loops back (same filename sent again)
    useEffect(() => {
        if (!currentMediaVideo?.isBroadcast)
            return;
        const videoElement = document.getElementById(`media-video-${currentMediaVideo.filename}`);
        if (!videoElement)
            return;
        // If video is at the end or paused, restart it
        if (videoElement.ended || videoElement.paused) {
            console.log('ðŸ”„ Restarting broadcast video from beginning:', currentMediaVideo.filename);
            videoElement.currentTime = 0;
            // Try to play at saved volume level
            videoElement.volume = mediaVolume / 100;
            videoElement.muted = mediaVolume === 0;
            const playPromise = videoElement.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                    console.log(`â–¶ï¸ Broadcast video restarted at ${mediaVolume}%`);
                })
                    .catch(error => {
                    // Browser blocked - fall back to muted AND update UI
                    console.log('âš ï¸ Browser blocked restart, trying muted');
                    videoElement.muted = true;
                    setGlobalVolume(0); // Update UI to show muted state
                    videoElement.play()
                        .then(() => console.log('â–¶ï¸ Broadcast video restarted (muted) - UI updated'))
                        .catch(err2 => console.error('âŒ Failed to restart even muted:', err2));
                });
            }
        }
    }, [currentMediaVideo, mediaVolume]);
    // Reset broadcastVideoPlaying when video changes
    useEffect(() => {
        setBroadcastVideoPlaying(false);
    }, [currentMediaVideo]);
    // STATE PRESERVATION: Handle seekTo for media files
    useEffect(() => {
        if (!currentMediaVideo || currentMediaVideo.seekTo === undefined || currentMediaVideo.seekTo === null)
            return;
        const videoElement = document.getElementById(`media-video-${currentMediaVideo.filename}`);
        if (!videoElement)
            return;
        // Wait for video to be ready (canplay event)
        const handleCanPlay = () => {
            if (currentMediaVideo.seekTo !== undefined && currentMediaVideo.seekTo !== null) {
                console.log(`â© Seeking media to ${currentMediaVideo.seekTo}s`);
                videoElement.currentTime = currentMediaVideo.seekTo;
                videoElement.play().catch(err => console.error('Failed to play after seek:', err));
            }
        };
        // If video is already ready, seek immediately
        if (videoElement.readyState >= 2) {
            handleCanPlay();
        }
        else {
            // Otherwise wait for canplay event
            videoElement.addEventListener('canplay', handleCanPlay, { once: true });
            return () => {
                videoElement.removeEventListener('canplay', handleCanPlay);
            };
        }
    }, [currentMediaVideo]);
    const handleYouTubeClick = (videoId) => {
        if (!isHost || !socket)
            return;
        console.log('ðŸŽ¬ Featured YouTube clicked, requesting play/pause toggle');
        socket.emit('youtube-control', { action: 'play-pause' });
    };
    const handleMediaClick = (filename) => {
        if (!isHost)
            return;
        const videoElement = document.getElementById(`media-video-${filename}`);
        if (videoElement) {
            if (videoElement.paused) {
                videoElement.play();
                console.log('â–¶ï¸ Host clicked featured media - playing');
            }
            else {
                videoElement.pause();
                console.log('â¸ï¸ Host clicked featured media - pausing');
            }
        }
    };
    const handleMediaCanPlay = (filename) => {
        console.log(`âœ… Media can play: ${filename}`);
        const videoElement = document.getElementById(`media-video-${filename}`);
        if (videoElement) {
            if (currentMediaVideo?.isBroadcast) {
                // Try to play at saved volume level (browser decides if allowed)
                videoElement.volume = mediaVolume / 100;
                videoElement.muted = mediaVolume === 0;
                console.log(`â–¶ï¸ Attempting autoplay at ${mediaVolume}% volume`);
                videoElement.play()
                    .then(() => {
                    console.log(`âœ… Broadcast video playing at ${mediaVolume}%`);
                    setBroadcastVideoPlaying(true);
                })
                    .catch(err => {
                    // Browser blocked unmuted autoplay - fall back to muted AND update UI
                    console.log('âš ï¸ Browser blocked unmuted autoplay, falling back to muted');
                    videoElement.muted = true;
                    setGlobalVolume(0); // Update UI to show muted state
                    videoElement.play()
                        .then(() => {
                        console.log('âœ… Broadcast video playing (muted fallback) - UI updated to show muted');
                        setBroadcastVideoPlaying(true);
                    })
                        .catch(err2 => {
                        console.error('âŒ Broadcast play failed even when muted:', err2);
                    });
                });
            }
            else if (isGloballyMuted) {
                videoElement.muted = true;
                console.log('ðŸ”‡ Video muted (global mute enabled)');
            }
            else {
                videoElement.muted = false;
                console.log('ðŸ”Š Media audio unmuted on canplay:', filename);
            }
        }
    };
    const handleMediaError = (filename, e) => {
        console.error(`âŒ Media error for ${filename}:`, e);
    };
    const handleBroadcastVideoEnded = () => {
        console.log('ðŸ“º Broadcast video ended, notifying server');
        if (socket && currentMediaVideo?.isBroadcast) {
            socket.emit('broadcast-video-ended', {
                filename: currentMediaVideo.filename
            });
        }
    };
    // ADDED: Determine if we should use side layout
    // Threshold lowered to 1500px to support 1710x1107 resolution
    // Calculation: 310px (left) + 3px + 854px (featured) + 3px + 310px (right) = 1480px minimum
    const useSideLayout = featuredMode && windowWidth >= 1500 && participants.length > 0;
    // ADDED: Split participants for side layout
    const leftWebcams = useSideLayout ? participants.slice(0, 2) : [];
    const rightWebcams = useSideLayout ? participants.slice(2, 4) : [];
    const belowWebcams = useSideLayout ? participants.slice(4) : participants;
    // CRITICAL FIX: Helper function to render a video tile with CSS-based positioning
    // This prevents unmount/remount when featured mode toggles
    const getVideoTileStyle = (participant, index) => {
        const participantIndex = participants.findIndex(p => p.id === participant.id);
        if (!useSideLayout) {
            // Normal grid layout - inline-block within grid
            return {};
        }
        // Featured mode side layout
        if (participantIndex < 2) {
            // Left column
            return {
                position: 'fixed',
                top: `${10 + 120 + (participantIndex * (180 + 3))}px`,
                left: 'calc(50% - 427px - 3px - 310px)',
                zIndex: 3000
            };
        }
        else if (participantIndex < 4) {
            // Right column
            return {
                position: 'fixed',
                top: `${10 + 120 + ((participantIndex - 2) * (180 + 3))}px`,
                left: 'calc(50% + 427px + 3px)',
                zIndex: 3000
            };
        }
        else {
            // Below in grid (normal positioning)
            return {};
        }
    };
    // Helper function to render a video tile with stable key and dynamic positioning
    const renderVideoTile = (participant, index) => {
        const customStyle = getVideoTileStyle(participant, index);
        return (_jsx("div", { style: customStyle, children: _jsx(VideoTile, { stream: participant.stream, username: participant.username, isLocal: participant.isLocal, showUsername: showUsernames, peerId: participant.peerId, streamType: participant.streamType, blurAmount: blurredUsers.get(participant.peerId) || 0, isAuthenticated: isAuthenticated, localCameraEnabled: cameraEnabled }, `video-tile-${participant.id}`) }, `video-tile-wrapper-${participant.id}`));
    };
    return (_jsxs("div", { className: "video-grid-container", children: [featuredMode && currentStream && (streamProxyUrl || streamDirectUrl) && (_jsx("div", { style: {
                    width: '854px',
                    height: '480px',
                    position: 'fixed',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 2000,
                    borderRadius: '3px',
                    overflow: 'hidden',
                    backgroundColor: '#000',
                    cursor: isHost ? 'pointer' : 'default',
                    border: '1px solid #8b5cf6',
                    boxSizing: 'border-box',
                    pointerEvents: 'auto'
                }, children: _jsx(StreamTile, { streamId: currentStream.id, proxyUrl: streamProxyUrl, directUrl: streamDirectUrl, title: currentStream.title, featuredMode: true, isHLS: streamIsHLS, isHost: isHost, socket: socket, isGloballyMuted: isGloballyMuted }) })), featuredMode && currentMediaVideo && (_jsxs("div", { className: "video-tile media-tile", style: {
                    width: featuredMode ? '854px' : '310px',
                    height: featuredMode ? '480px' : '180px',
                    position: featuredMode ? 'fixed' : 'relative',
                    top: featuredMode ? '10px' : 'auto',
                    left: featuredMode ? '50%' : 'auto',
                    transform: featuredMode ? 'translateX(-50%)' : 'none',
                    zIndex: featuredMode ? 2000 : 1,
                    borderRadius: '3px',
                    overflow: 'hidden',
                    backgroundColor: '#000',
                    cursor: (isHost && !currentMediaVideo.isBroadcast) ? 'pointer' : 'default',
                    border: '1px solid #8b5cf6',
                    boxSizing: 'border-box',
                    pointerEvents: 'auto'
                }, onClick: currentMediaVideo.isBroadcast ? undefined : () => handleMediaClick(currentMediaVideo.filename), onMouseEnter: () => setMediaIsHovered(true), onMouseLeave: () => setMediaIsHovered(false), children: [!isHost && (_jsx("div", { style: {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'transparent',
                            zIndex: 10,
                            pointerEvents: 'auto'
                        } })), _jsx("video", { id: `media-video-${currentMediaVideo.filename}`, className: "media-video-element", src: currentMediaVideo.path || currentMediaVideo.url || `/media/${currentMediaVideo.filename}`, style: {
                            width: '100%',
                            height: '100%',
                            objectFit: featuredMode ? 'contain' : 'cover',
                            pointerEvents: 'none'
                        }, autoPlay: !currentMediaVideo.isBroadcast, muted: true, loop: false, playsInline: true, onError: (e) => handleMediaError(currentMediaVideo.filename, e), onLoadStart: () => console.log(`ðŸ“‚ Media loading: ${currentMediaVideo.filename}`), onLoadedData: () => console.log(`ðŸ“‚ Media loaded: ${currentMediaVideo.filename}`), onCanPlay: () => handleMediaCanPlay(currentMediaVideo.filename), onEnded: currentMediaVideo.isBroadcast ? handleBroadcastVideoEnded : undefined, onPlay: (e) => {
                            console.log(`â–¶ï¸ Media playing: ${currentMediaVideo.filename}`);
                            // Non-broadcast videos can unmute immediately
                            if (!currentMediaVideo.isBroadcast && !isGloballyMuted) {
                                const videoElement = e.target;
                                if (videoElement) {
                                    videoElement.muted = false;
                                    console.log('ðŸ”Š Non-broadcast media audio unmuted on play event:', currentMediaVideo.filename);
                                }
                            }
                        }, onPause: () => console.log(`â¸ï¸ Media paused: ${currentMediaVideo.filename}`) }), _jsx("div", { style: {
                            opacity: mediaVolume === 0 ? 1 : (mediaIsHovered ? 1 : 0),
                            transition: 'opacity 0.2s ease',
                            pointerEvents: mediaVolume === 0 ? 'auto' : (mediaIsHovered ? 'auto' : 'none'),
                            width: '100%', // Make container fill the width
                            position: 'absolute',
                            bottom: 0,
                            left: 0
                        }, children: _jsx(CompactVolumeSlider, { volume: mediaVolume, onVolumeChange: setGlobalVolume, isFeatured: true }) })] })), _jsxs("div", { className: "video-grid", style: {
                    paddingTop: featuredMode && !useSideLayout ? '493px' : undefined
                }, children: [_jsx(RemoteTile, { socket: socket, isHost: isHost, featuredMode: featuredMode }), currentYouTubeVideo && (_jsxs("div", { style: {
                            width: featuredMode ? '854px' : '310px',
                            height: featuredMode ? '480px' : '180px',
                            position: featuredMode ? 'fixed' : 'relative',
                            top: featuredMode ? '10px' : 'auto',
                            left: featuredMode ? '50%' : 'auto',
                            transform: featuredMode ? 'translateX(-50%)' : 'none',
                            zIndex: featuredMode ? 2000 : 1,
                            borderRadius: '3px',
                            overflow: 'hidden',
                            backgroundColor: '#000',
                            cursor: isHost ? 'pointer' : 'default',
                            border: '1px solid #8b5cf6',
                            boxSizing: 'border-box',
                            pointerEvents: 'auto',
                            display: featuredMode ? 'block' : 'inline-block'
                        }, children: [_jsx(YouTubeTile, { videoId: currentYouTubeVideo.videoId, username: "YouTube", showUsername: featuredMode ? false : showUsernames, isProducer: isYouTubeProducer, isHost: isHost, socket: socket, onPlay: onYouTubePlay, onPause: onYouTubePause, onStop: onYouTubeStop, onClick: featuredMode ? () => handleYouTubeClick(currentYouTubeVideo.videoId) : onYouTubeClick, onCommand: onYouTubeCommand, startTime: youtubeStartTime, isPaused: youtubeIsPaused, pausedAt: youtubePausedAt, isGloballyMuted: isGloballyMuted, seekTo: currentYouTubeVideo.seekTo, isBroadcast: currentYouTubeVideo.isBroadcast || false, isFeatured: featuredMode }, "youtube-player-singleton"), _jsx("style", { children: `
              .youtube-tile {
                width: 100% !important;
                height: 100% !important;
              }
            ` })] })), !featuredMode && currentMediaVideo && (_jsxs("div", { className: "video-tile media-tile", style: {
                            width: '310px',
                            height: '180px',
                            position: 'relative',
                            borderRadius: '3px',
                            overflow: 'hidden',
                            backgroundColor: '#000',
                            cursor: (isHost && !currentMediaVideo.isBroadcast) ? 'pointer' : 'default',
                            border: '1px solid #8b5cf6',
                            boxSizing: 'border-box',
                            pointerEvents: 'auto'
                        }, onClick: currentMediaVideo.isBroadcast ? undefined : () => handleMediaClick(currentMediaVideo.filename), onMouseEnter: () => setMediaIsHovered(true), onMouseLeave: () => setMediaIsHovered(false), children: [!isHost && (_jsx("div", { style: {
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: 'transparent',
                                    zIndex: 10,
                                    pointerEvents: 'auto'
                                } })), _jsx("video", { id: `media-video-${currentMediaVideo.filename}`, className: "media-video-element", src: currentMediaVideo.path || currentMediaVideo.url || `/media/${currentMediaVideo.filename}`, style: {
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    pointerEvents: 'none'
                                }, autoPlay: !currentMediaVideo.isBroadcast, muted: true, loop: false, playsInline: true, onError: (e) => handleMediaError(currentMediaVideo.filename, e), onLoadStart: () => console.log(`ðŸ“‚ Media loading: ${currentMediaVideo.filename}`), onLoadedData: () => console.log(`ðŸ“‚ Media loaded: ${currentMediaVideo.filename}`), onCanPlay: () => handleMediaCanPlay(currentMediaVideo.filename), onEnded: currentMediaVideo.isBroadcast ? handleBroadcastVideoEnded : undefined, onPlay: (e) => {
                                    console.log(`â–¶ï¸ Media playing: ${currentMediaVideo.filename}`);
                                    // Non-broadcast videos can unmute immediately
                                    if (!currentMediaVideo.isBroadcast && !isGloballyMuted) {
                                        const videoElement = e.target;
                                        if (videoElement) {
                                            videoElement.muted = false;
                                            console.log('ðŸ”Š Non-broadcast media audio unmuted on play event:', currentMediaVideo.filename);
                                        }
                                    }
                                }, onPause: () => console.log(`â¸ï¸ Media paused: ${currentMediaVideo.filename}`) }), _jsx("div", { style: {
                                    opacity: mediaVolume === 0 ? 1 : (mediaIsHovered ? 1 : 0),
                                    transition: 'opacity 0.2s ease',
                                    pointerEvents: mediaVolume === 0 ? 'auto' : (mediaIsHovered ? 'auto' : 'none')
                                }, children: _jsx(CompactVolumeSlider, { volume: mediaVolume, onVolumeChange: setGlobalVolume, isFeatured: false }) })] })), !featuredMode && currentStream && (streamProxyUrl || streamDirectUrl) && (_jsx(StreamTile, { streamId: currentStream.id, proxyUrl: streamProxyUrl, directUrl: streamDirectUrl, title: currentStream.title, featuredMode: false, isHLS: streamIsHLS, isHost: isHost, socket: socket, isGloballyMuted: isGloballyMuted })), participants.map(renderVideoTile)] })] }));
};
export default VideoGrid;
